<template>
  <div v-if="open" class="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" @click.self="$emit('cancel')">
    <div class="bg-white rounded-lg shadow-xl w-full max-w-md p-5">
      <h2 class="text-lg font-semibold mb-1">Schedule send{{ count > 1 ? ` (${count} drafts)` : '' }}</h2>
      <p class="text-sm text-gray-500 mb-4">
        Pick when {{ count > 1 ? 'these drafts' : 'this draft' }} should send automatically.
        The time is in your local timezone.
      </p>

      <div class="flex flex-wrap gap-2 mb-3">
        <button
          v-for="preset in presets"
          :key="preset.label"
          type="button"
          @click="applyPreset(preset)"
          class="px-2.5 py-1 rounded-full text-xs border border-gray-300 bg-white hover:bg-gray-100"
        >{{ preset.label }}</button>
      </div>

      <label class="block text-xs font-medium text-gray-600 mb-1">Send at</label>
      <input
        v-model="localValue"
        type="datetime-local"
        :min="minLocal"
        class="w-full border rounded-md p-2 text-sm"
      />

      <p v-if="preview" class="text-xs mt-2" :class="isFuture ? 'text-gray-500' : 'text-red-500'">
        <template v-if="isFuture">Will send {{ preview }}</template>
        <template v-else>Pick a time in the future.</template>
      </p>

      <div class="flex justify-end gap-2 mt-5">
        <button
          type="button"
          @click="$emit('cancel')"
          class="px-3 py-1.5 rounded-md text-sm border border-gray-300 bg-white hover:bg-gray-100"
        >Cancel</button>
        <button
          type="button"
          @click="confirm"
          :disabled="submitting || !isFuture"
          class="px-3 py-1.5 rounded-md text-sm bg-dark-primary text-white disabled:opacity-40"
        >{{ submitting ? 'Scheduling…' : 'Schedule' }}</button>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, watch, defineProps, defineEmits } from 'vue'

const props = defineProps({
  open: { type: Boolean, default: false },
  count: { type: Number, default: 1 },
  submitting: { type: Boolean, default: false },
})
const emit = defineEmits(['confirm', 'cancel'])

// A datetime-local <input> holds a local, timezone-naive "YYYY-MM-DDTHH:mm"
// string. We convert to an absolute instant only at confirm time, so the moment
// the analyst picked is preserved exactly regardless of their timezone.
const localValue = ref('')

function pad(n) { return String(n).padStart(2, '0') }

// Local "YYYY-MM-DDTHH:mm" for a Date, the format a datetime-local input expects.
function toLocalInput(date) {
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`
    + `T${pad(date.getHours())}:${pad(date.getMinutes())}`
}

// Next occurrence of a given hour, tomorrow if today's is already past. When
// weekday is set (0=Sun..6=Sat), advances to the next such weekday instead.
function nextAt(hour, minute = 0, weekday = null) {
  const d = new Date()
  d.setSeconds(0, 0)
  d.setHours(hour, minute, 0, 0)
  if (weekday === null) {
    if (d <= new Date()) d.setDate(d.getDate() + 1)
  } else {
    // Advance to the target weekday; if that's today but already past, jump a week.
    let delta = (weekday - d.getDay() + 7) % 7
    if (delta === 0 && d <= new Date()) delta = 7
    d.setDate(d.getDate() + delta)
  }
  return d
}

const presets = [
  { label: 'In 1 hour', build: () => { const d = new Date(); d.setHours(d.getHours() + 1); d.setSeconds(0, 0); return d } },
  { label: 'Tomorrow 9 AM', build: () => nextAt(9, 0) },
  { label: 'Tomorrow 1 PM', build: () => nextAt(13, 0) },
  { label: 'Monday 9 AM', build: () => nextAt(9, 0, 1) },
]

function applyPreset(preset) {
  localValue.value = toLocalInput(preset.build())
}

// datetime-local's own floor, so the native picker discourages past times too.
const minLocal = computed(() => toLocalInput(new Date()))

const parsedDate = computed(() => {
  if (!localValue.value) return null
  const d = new Date(localValue.value) // interpreted as local time
  return isNaN(d.getTime()) ? null : d
})
const isFuture = computed(() => !!parsedDate.value && parsedDate.value > new Date())
const preview = computed(() => parsedDate.value ? parsedDate.value.toLocaleString() : '')

// Default the picker to tomorrow 9 AM each time it opens.
watch(() => props.open, (isOpen) => {
  if (isOpen) localValue.value = toLocalInput(nextAt(9, 0))
})

function confirm() {
  if (!isFuture.value) return
  // Absolute UTC instant with offset baked in, so the server stores the exact
  // moment picked.
  emit('confirm', parsedDate.value.toISOString())
}
</script>
